import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Loader2, XCircle, Zap } from "lucide-react";
import api from "@/lib/api";

export default function PaymentSuccess() {
  const [status, setStatus] = useState("checking");
  const { refreshUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setStatus("success");
          refreshUser().catch(() => {});
          return;
        }
        if (["expired", "failed"].includes(res.data.payment_status)) {
          setStatus("error");
          return;
        }
      } catch (e) {
        // keep polling
      }
      if (attempts >= 8) {
        setStatus("pending");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
  }, []);

  return (
    <div className="dark min-h-screen bg-anthracite-900 text-white flex items-center justify-center px-6">
      <div className="glass rounded-3xl p-10 max-w-md w-full text-center">
        <Link to="/" className="inline-flex items-center gap-2 mb-6">
          <Zap className="w-6 h-6 text-electric" strokeWidth={1.5} />
          <span className="font-heading text-xl font-semibold">Auto<span className="text-electric">Park</span></span>
        </Link>

        {status === "checking" && (
          <>
            <Loader2 className="w-14 h-14 text-electric animate-spin mx-auto" />
            <h1 className="font-heading text-2xl font-light mt-5">Fizetés ellenőrzése…</h1>
            <p className="text-slate-400 mt-2">Egy pillanat, megerősítjük a tranzakciót.</p>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-electric mx-auto" strokeWidth={1.5} data-testid="payment-success" />
            <h1 className="font-heading text-2xl font-light mt-5">Sikeres kreditvásárlás!</h1>
            <p className="text-slate-400 mt-2">A krediteket jóváírtuk a fiókodon. Most már feladhatsz hirdetést.</p>
            <button onClick={() => navigate("/fiok")} className="mt-6 w-full py-3.5 rounded-full bg-electric hover:bg-electric-light text-white font-medium transition-all">
              Tovább a fiókomhoz
            </button>
          </>
        )}

        {status === "pending" && (
          <>
            <Loader2 className="w-14 h-14 text-electric mx-auto" />
            <h1 className="font-heading text-2xl font-light mt-5">Feldolgozás folyamatban</h1>
            <p className="text-slate-400 mt-2">A fizetés feldolgozása tovább tart a szokásosnál. Nézd meg a fiókodat pár perc múlva.</p>
            <button onClick={() => navigate("/fiok")} className="mt-6 w-full py-3.5 rounded-full glass hover:bg-white/10 font-medium transition-all">
              Tovább a fiókomhoz
            </button>
          </>
        )}

        {status === "error" && (
          <>
            <XCircle className="w-14 h-14 text-red-400 mx-auto" strokeWidth={1.5} />
            <h1 className="font-heading text-2xl font-light mt-5">A fizetés nem sikerült</h1>
            <p className="text-slate-400 mt-2">Nem történt terhelés. Próbáld újra a kreditek oldalon.</p>
            <button onClick={() => navigate("/kreditek")} className="mt-6 w-full py-3.5 rounded-full bg-electric hover:bg-electric-light text-white font-medium transition-all">
              Vissza a kreditekhez
            </button>
          </>
        )}
      </div>
    </div>
  );
}
